import React from 'react';
import PropTypes from 'prop-types';
import { Button, TextField, MenuItem, Typography } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { ChevronLeft, ChevronRight } from '@material-ui/icons';
import { PAGINATION_OPTIONS } from '../constants';

const useStyles = makeStyles(theme => ({
  paginationContainer: {
    background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)',
    borderTop: '1px solid #e2e8f0',
    borderBottomLeftRadius: 20,
    borderBottomRightRadius: 20,
    padding: theme.spacing(2.5, 3),
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: 16,
    boxShadow: '0 -2px 8px -4px rgba(0,0,0,0.08)'
  },
  paginationBtn: {
    borderRadius: 14,
    textTransform: 'none',
    fontWeight: 500,
    padding: '8px 18px',
    border: '1px solid #cbd5e0',
    background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
    color: '#4a5568',
    transition: 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
    boxShadow: '0 2px 6px -2px rgba(0,0,0,0.12)',
    '&:hover:not(:disabled)': {
      background: 'linear-gradient(135deg, #f1f5f9 0%, #e2e8f0 100%)',
      borderColor: '#9ca3af',
      boxShadow: '0 4px 12px -4px rgba(0,0,0,0.18)',
      transform: 'translateY(-1px)'
    },
    '&:disabled': {
      opacity: 0.4,
      cursor: 'not-allowed',
      transform: 'none',
      boxShadow: 'none'
    }
  },
  paginationSelect: {
    '& .MuiOutlinedInput-root': {
      borderRadius: 12,
      background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
      fontSize: 14,
      fontWeight: 500,
      boxShadow: '0 2px 6px -2px rgba(0,0,0,0.08)',
      '& fieldset': { borderColor: '#cbd5e0' },
      '&:hover fieldset': { 
        borderColor: '#9ca3af',
        boxShadow: '0 0 0 3px rgba(156,163,175,0.1)'
      },
      '&.Mui-focused fieldset': { 
        borderColor: '#4285f4', 
        boxShadow: '0 0 0 3px rgba(66,133,244,0.15)' 
      }
    },
    '& .MuiInputLabel-outlined': {
      fontWeight: 500,
      fontSize: 14
    }
  }
}));

/**
 * Contrôles de pagination
 */
const PaginationControls = ({ metrics, itemsPerPage, onPageChange, onItemsPerPageChange }) => {
  const classes = useStyles();

  if (!metrics || metrics.totalItems <= itemsPerPage) {
    return null;
  }

  return (
    <div className={classes.paginationContainer}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
        <TextField
          select
          label="Lignes par page"
          value={itemsPerPage}
          onChange={e => onItemsPerPageChange(Number(e.target?.value || 25))}
          variant="outlined"
          size="small"
          className={classes.paginationSelect}
          style={{ width: 140 }}
        >
          {PAGINATION_OPTIONS.map(n => (
            <MenuItem key={n} value={n}>{n}</MenuItem>
          ))}
        </TextField>
        
        <Typography variant="body2" style={{ color: '#718096', fontWeight: 500, fontSize: 13 }}>
          Total: {metrics.totalItems} • Page {metrics.currentPage + 1} sur {metrics.totalPages}
        </Typography>
      </div>
      
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <Button 
          className={classes.paginationBtn}
          disabled={!metrics.hasPrev} 
          onClick={() => onPageChange(p => p - 1)}
          startIcon={<ChevronLeft />}
        >
          Précédent
        </Button>
        
        <Button 
          className={classes.paginationBtn}
          disabled={!metrics.hasNext} 
          onClick={() => onPageChange(p => p + 1)}
          endIcon={<ChevronRight />}
        >
          Suivant
        </Button>
      </div>
    </div>
  );
};

PaginationControls.propTypes = {
  metrics: PropTypes.shape({
    totalItems: PropTypes.number.isRequired,
    totalPages: PropTypes.number.isRequired,
    currentPage: PropTypes.number.isRequired,
    hasNext: PropTypes.bool.isRequired,
    hasPrev: PropTypes.bool.isRequired
  }),
  itemsPerPage: PropTypes.number.isRequired,
  onPageChange: PropTypes.func.isRequired,
  onItemsPerPageChange: PropTypes.func.isRequired
};

export default PaginationControls;
